import { FormValues, WidgetTypes } from "@/types/widget";
import { Widget, WidgetConfiguration } from "@/lib/api";

/**
 * Marker identifying a widget payload on the system clipboard. Versioned so the
 * format can evolve while still allowing older/newer payloads to be rejected
 * cleanly when copy/pasting between deployment environments.
 */
export const WIDGET_CLIPBOARD_MARKER = "knmi.widget.v1";

/** Grid size of the source container, carried along for paste-as-new-container. */
export interface WidgetClipboardLayout {
  width: number;
  height: number;
}

/**
 * Supported container grid bounds. Width is capped by the 12-column grid
 * (`WidgetGridLayout` `cols`) with the admin editor minimum (`minW: 3`); height
 * uses the admin editor minimum (`minH: 6`) with a generous cap so unusually
 * tall copies stay usable rather than being rejected outright.
 */
export const WIDGET_LAYOUT_BOUNDS = {
  minWidth: 3,
  maxWidth: 12,
  minHeight: 6,
  maxHeight: 50,
} as const;

/** Result of reading a widget from the clipboard. */
export interface ParsedWidget {
  values: FormValues;
  /** Present when the copied widget carried its source container size. */
  layout?: WidgetClipboardLayout;
}

interface WidgetClipboardEnvelope {
  marker: typeof WIDGET_CLIPBOARD_MARKER;
  version: 1;
  widget: {
    title: string;
    configuration: WidgetConfiguration;
  };
  layout?: WidgetClipboardLayout;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

function isWidgetType(value: unknown): value is WidgetTypes {
  return (
    typeof value === "string" && (Object.values(WidgetTypes) as string[]).includes(value)
  );
}

/**
 * Rejects non-numeric, non-positive and non-integer dimensions (returns
 * `null`); a valid positive integer is clamped into the supported range so a
 * slightly-off or unusually large copy stays usable.
 */
function clampDimension(value: unknown, min: number, max: number): number | null {
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) return null;
  return Math.min(Math.max(value, min), max);
}

function readLayout(value: unknown): WidgetClipboardLayout | undefined {
  const layout = readRecord(value);
  if (!layout) return undefined;

  const width = clampDimension(
    layout.width,
    WIDGET_LAYOUT_BOUNDS.minWidth,
    WIDGET_LAYOUT_BOUNDS.maxWidth,
  );
  const height = clampDimension(
    layout.height,
    WIDGET_LAYOUT_BOUNDS.minHeight,
    WIDGET_LAYOUT_BOUNDS.maxHeight,
  );

  // Either dimension being invalid rejects the whole size payload.
  if (width === null || height === null) return undefined;
  return { width, height };
}

/**
 * Serializes the portable part of a widget (title + configuration) into a
 * clipboard string. The server-assigned `id` and container placement are
 * intentionally excluded so the payload can be pasted anywhere; the source
 * container size is included so a paste-as-new-container can restore it.
 */
export function serializeWidgetForClipboard(
  widget: Widget,
  layout?: WidgetClipboardLayout,
): string {
  const envelope: WidgetClipboardEnvelope = {
    marker: WIDGET_CLIPBOARD_MARKER,
    version: 1,
    widget: {
      title: widget.title,
      configuration: widget.configuration as WidgetConfiguration,
    },
    ...(layout ? { layout } : {}),
  };
  return JSON.stringify(envelope);
}

/**
 * Parses clipboard text back into a widget plus optional layout. Returns `null`
 * for any text that is not a valid, recognised widget payload (invalid JSON,
 * wrong marker, or an unknown widget type), so callers can surface a single
 * error. An invalid/absent layout is dropped without failing the whole parse.
 */
export function parseWidgetClipboardText(text: string): ParsedWidget | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }

  const envelope = readRecord(parsed);
  if (!envelope || envelope.marker !== WIDGET_CLIPBOARD_MARKER) return null;

  const widget = readRecord(envelope.widget);
  if (!widget || typeof widget.title !== "string") return null;

  const configuration = readRecord(widget.configuration);
  if (!configuration || !isWidgetType(configuration.type)) return null;

  return {
    values: {
      title: widget.title,
      configuration: configuration as unknown as WidgetConfiguration,
    } as FormValues,
    layout: readLayout(envelope.layout),
  };
}
